<template>
    <div>
        <app-tabela :itens="anexosMedicao" :campos="camposTabela" :itensPorPagina="10" :bordered="false" :small="false" :mostrarFiltro="mostrarFiltro">
            <template slot="tamanho" slot-scope="data">
                {{ data.tamanho | tamanhoArquivoFormatado }}
            </template>

            <template slot="botoes">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <h5 class="mb-0">Anexos</h5>
                    <div>
                        <input
                            ref="inputAnexoComplementar"
                            type="file"
                            multiple
                            style="display: none"
                            :accept="acceptAnexoComplementar"
                            @change="uploadAnexosComplementares"
                        />
                        <b-button
                            v-if="editavel"
                            class="mr-2"
                            variant="primary"
                            :disabled="salvandoAnexoComplementar"
                            @click="abrirSeletorAnexoComplementar"
                        >
                            <span v-if="salvandoAnexoComplementar">
                                <i class="fa fa-spinner fa-spin mr-2"></i>Enviando...
                            </span>
                            <span v-else>
                                <i class="fa fa-plus mr-2"></i>Adicionar anexos complementares
                            </span>
                        </b-button>
                        <b-button
                            v-if="anexosMedicao.length > 0"
                            @click="baixarTodos"
                        >
                            Baixar todos
                        </b-button>
                    </div>
                </div>
            </template>

            <template slot="data" slot-scope="data">
                {{ data.data | toDate }}
            </template>

            <template slot="acoes" slot-scope="data">
                <span class="text-primary">
                    <i class="fa fa-download fa-lg icon-button" title="Download" @click.stop="downloadAnexo(data)"></i>
                </span>
                <span v-if="editavel" class="text-danger ml-3">
                    <i class="fa fa-trash fa-lg icon-button" title="Excluir" @click.stop="excluirAnexo(data)"></i>
                </span>
            </template>
        </app-tabela>

        <b-modal
            v-model="modalRenomearVisivel"
            title="Renomear arquivo antes do envio"
            :no-close-on-backdrop="true"
            :no-close-on-esc="true"
            hide-header-close
            ok-title="Confirmar renomeação"
            cancel-title="Pular este arquivo"
            :ok-disabled="!novoNomeArquivoValido"
            @ok="confirmarRenomear"
            @cancel="cancelarRenomear"
        >
            <b-alert variant="warning" show>
                <strong>Nome do arquivo muito longo.</strong>
                Este arquivo será integrado ao <strong>SEI</strong>, que aceita apenas nomes com no máximo
                <strong>50 caracteres</strong>. Por favor, informe um nome mais curto para prosseguir com o envio.
            </b-alert>

            <p class="mt-3 mb-1 text-break">
                <strong>Arquivo original:</strong>
                {{ renomearArquivoAtual && renomearArquivoAtual.name }}
                <b-badge variant="danger" class="ml-1">{{ renomearArquivoAtual && renomearArquivoAtual.name.length }} caracteres</b-badge>
            </p>

            <b-form-group label="Novo nome do arquivo" class="mt-3">
                <b-input-group :append="extensaoArquivoAtual">
                    <b-form-input
                        v-model="novoNomeArquivo"
                        :state="novoNomeArquivo ? novoNomeArquivoValido : null"
                        placeholder="Digite o novo nome (sem extensão)"
                        autofocus
                    />
                </b-input-group>
                <b-form-text :class="charCountClass">
                    {{ (novoNomeArquivo + extensaoArquivoAtual).length }} / 50 caracteres
                </b-form-text>
                <b-form-invalid-feedback :state="novoNomeArquivo ? novoNomeArquivoValido : null">
                    O nome com a extensão excede 50 caracteres.
                </b-form-invalid-feedback>
            </b-form-group>

            <p v-if="filaRenomear.length > 1" class="text-muted mt-2">
                <small>{{ filaRenomear.length - 1 }} arquivo(s) aguardando renomeação após este.</small>
            </p>
        </b-modal>
    </div>
</template>

<script>

import BoletimMedicaoServicos from '../../BoletimMedicaoServicos.js'
import { tiposAnexoEnum } from '../../tiposAnexo.js'

export default {
    props: {
        contrato: {
            type: Object,
            default: null
        },
        boletimMedicao: {
            type: Object,
            default: null
        },
        editavel: {
            type: Boolean,
            default: false
        },

        anexosMedicao: {
            type: Array,
            default: () => []
        },

        mostrarFiltro: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            todosSelecionados: false,
            extensoesPermitidasAnexoComplementar: [
                '.doc', '.docx', '.xls', '.xlsx', '.pdf', '.jpg', '.jpeg', '.zip'
            ],
            tamanhoMaximoAnexoComplementarBytes: 50 * 1024 * 1024,
            camposTabela: [
                {
                    label: 'Arquivo',
                    key: 'nomeDocumento',
                    sortable: true,
                    filterable: true,
                },
                {
                    label: 'Tipo',
                    key: 'tipoAnexoBoletimMedicaoDescricao',
                    sortable: true,
                    filterable: true,
                },
                {
                    label: 'Data',
                    key: 'data',
                    sortable: true,
                },
                {
                    label: 'Tamanho',
                    key: 'tamanho',
                    sortable: true,
                },
                {
                    label: 'Ações',
                    key: 'acoes',
                }
            ],
            erros: [],
            salvandoAnexoComplementar: false,
            modalRenomearVisivel: false,
            filaRenomear: [],
            renomearArquivoAtual: null,
            novoNomeArquivo: '',
            arquivosParaUpload: [],
        }
    },

    computed: {
        acceptAnexoComplementar() {
            return this.extensoesPermitidasAnexoComplementar.join(',')
        },
        extensaoArquivoAtual() {
            if (!this.renomearArquivoAtual) return ''
            const nome = this.renomearArquivoAtual.name || ''
            const idx = nome.lastIndexOf('.')
            return idx >= 0 ? nome.substring(idx) : ''
        },
        novoNomeArquivoValido() {
            if (!this.novoNomeArquivo || !this.novoNomeArquivo.trim()) return false
            const nomeCompleto = this.novoNomeArquivo.trim() + this.extensaoArquivoAtual
            return nomeCompleto.length <= 50
        },
        charCountClass() {
            const total = this.novoNomeArquivo.length + this.extensaoArquivoAtual.length
            return total > 50 ? 'text-danger font-weight-bold' : 'text-muted'
        }
    },

    methods: {
        abrirSeletorAnexoComplementar() {
            if (!this.editavel || this.salvandoAnexoComplementar) return
            this.$refs.inputAnexoComplementar?.click()
        },

        async uploadAnexosComplementares(event) {
            if (!this.editavel || this.salvandoAnexoComplementar) return
            if (!this.boletimMedicao || !this.boletimMedicao.boletimMedicaoId) return
            if (!this.contrato || !this.contrato.contratoId) return

            const arquivos = Array.from(event.target.files || [])
            if (!arquivos.length) return

            const arquivosInvalidosPorExtensao = arquivos.filter((arquivo) => {
                const nomeArquivo = (arquivo.name || '').toLowerCase()
                const indiceUltimoPonto = nomeArquivo.lastIndexOf('.')
                if (indiceUltimoPonto < 0) return true

                const extensao = nomeArquivo.substring(indiceUltimoPonto)
                return !this.extensoesPermitidasAnexoComplementar.includes(extensao)
            })

            const arquivosInvalidosPorTamanho = arquivos.filter(arquivo =>
                (arquivo?.size || 0) > this.tamanhoMaximoAnexoComplementarBytes
            )

            const arquivosBaseValidos = arquivos.filter(arquivo =>
                !arquivosInvalidosPorExtensao.includes(arquivo) &&
                !arquivosInvalidosPorTamanho.includes(arquivo)
            )

            if (arquivosInvalidosPorExtensao.length > 0) {
                this.$toast.error(
                    `Tipos não permitidos: ${arquivosInvalidosPorExtensao.map(a => a.name).join(', ')}`,
                    'Erro',
                    this.$toastConfig.error
                )
            }

            if (arquivosInvalidosPorTamanho.length > 0) {
                this.$toast.error(
                    `Arquivos acima de 50 MB: ${arquivosInvalidosPorTamanho.map(a => a.name).join(', ')}`,
                    'Erro',
                    this.$toastConfig.error
                )
            }

            if (!arquivosBaseValidos.length) {
                if (this.$refs.inputAnexoComplementar) {
                    this.$refs.inputAnexoComplementar.value = ''
                }
                return
            }

            const arquivosNomeOK = arquivosBaseValidos.filter(a => a.name.length <= 50)
            const arquivosNomeLongo = arquivosBaseValidos.filter(a => a.name.length > 50)

            this.arquivosParaUpload = [...arquivosNomeOK]
            this.filaRenomear = [...arquivosNomeLongo]

            if (this.filaRenomear.length > 0) {
                this.proximoRenomear()
            } else {
                this.executarUpload()
            }
        },

        proximoRenomear() {
            if (this.filaRenomear.length === 0) {
                this.executarUpload()
                return
            }

            this.renomearArquivoAtual = this.filaRenomear[0]

            const ext = this.extensaoArquivoAtual
            const nomeOriginalSemExt = ext
                ? this.renomearArquivoAtual.name.slice(0, -ext.length)
                : this.renomearArquivoAtual.name

            const maxNome = 50 - ext.length
            this.novoNomeArquivo = nomeOriginalSemExt.substring(0, maxNome)

            this.modalRenomearVisivel = true
        },

        confirmarRenomear(bvModalEvent) {
            if (!this.novoNomeArquivoValido) {
                bvModalEvent.preventDefault()
                return
            }

            const nomeCompleto = this.novoNomeArquivo.trim() + this.extensaoArquivoAtual
            const arquivoRenomeado = new File(
                [this.renomearArquivoAtual],
                nomeCompleto,
                { type: this.renomearArquivoAtual.type }
            )

            this.arquivosParaUpload.push(arquivoRenomeado)
            this.filaRenomear.shift()
            this.renomearArquivoAtual = null
            this.novoNomeArquivo = ''

            this.$nextTick(() => this.proximoRenomear())
        },

        cancelarRenomear() {
            this.filaRenomear.shift()
            this.renomearArquivoAtual = null
            this.novoNomeArquivo = ''

            this.$nextTick(() => this.proximoRenomear())
        },

        async executarUpload() {
            if (!this.arquivosParaUpload.length) {
                if (this.$refs.inputAnexoComplementar) {
                    this.$refs.inputAnexoComplementar.value = ''
                }
                return
            }

            this.salvandoAnexoComplementar = true
            this.$store.dispatch('addRequest')

            try {
                let quantidadeSucesso = 0
                let quantidadeErro = 0

                for (const arquivo of this.arquivosParaUpload) {
                    const response = await BoletimMedicaoServicos.adicionarDocumentoBoletimMedicao(
                        this.boletimMedicao.contratoId || this.contrato.contratoId,
                        this.boletimMedicao.boletimMedicaoId,
                        {
                            tipo: tiposAnexoEnum.OutrosDocumentos,
                            arquivo
                        }
                    )

                    if (response && response.success) {
                        quantidadeSucesso++
                    } else {
                        quantidadeErro++
                    }
                }

                if (quantidadeSucesso > 0) {
                    this.$toast.success('Anexos complementares adicionados com sucesso!', 'OK', this.$toastConfig.success)
                    this.$emit('recarregarDadosBoletim')
                }

                if (quantidadeErro > 0) {
                    this.$toast.error('Não foi possível adicionar os anexos complementares', 'Erro', this.$toastConfig.error)
                }
            } catch (error) {
                this.$toast.error('Não foi possível adicionar os anexos complementares', 'Erro', this.$toastConfig.error)
            } finally {
                this.salvandoAnexoComplementar = false
                this.$store.dispatch('removeRequest')
                this.arquivosParaUpload = []
                if (this.$refs.inputAnexoComplementar) {
                    this.$refs.inputAnexoComplementar.value = ''
                }
            }
        },

        downloadAnexo(documentoBoletimMedicao) {
            this.$store.dispatch('addRequest')

            BoletimMedicaoServicos.downloadAnexo(this.contrato.contratoId, documentoBoletimMedicao.boletimMedicaoId, documentoBoletimMedicao.documentoBoletimMedicaoId)
                .then(response => {
                    if (!response || !(response instanceof Blob)) {
                        this.$toast.error(
                            'Não foi possível fazer o download do arquivo',
                            'Erro',
                            this.$toastConfig.error
                        )
                        return
                    }

                    const responseType = (response.type || '').toLowerCase()
                    if (responseType.includes('json') || responseType.includes('text')) {
                        this.$toast.error(
                            'Não foi possível fazer o download do arquivo',
                            'Erro',
                            this.$toastConfig.error
                        )
                        return
                    }

                    const caminhoDocumento =
                        documentoBoletimMedicao.caminhoDocumento || ''

                    const nomeArquivoDoCaminho = caminhoDocumento
                        ? caminhoDocumento.split('/').pop().split('\\').pop()
                        : ''

                    const nomeArquivo =
                        documentoBoletimMedicao.nomeDocumento ||
                        documentoBoletimMedicao.nomeArquivo ||
                        documentoBoletimMedicao.nome ||
                        nomeArquivoDoCaminho ||
                        `anexo-${documentoBoletimMedicao.documentoBoletimMedicaoId || 'boletim-medicao'}`

                    this.$download(response, nomeArquivo)
                })
                .catch(error => {
                    this.$toast.error(
                        'Não foi possível fazer o download do arquivo',
                        'Erro',
                        this.$toastConfig.error
                    )
                })
                .finally(() => {
                    this.$store.dispatch('removeRequest')
                })
        },

        excluirAnexo(documentoBoletimMedicao) {
            if (!this.editavel) return

            const confirmouExclusao = window.confirm('Deseja realmente excluir este anexo?')
            if (!confirmouExclusao) return

            this.$store.dispatch('addRequest')

            BoletimMedicaoServicos.excluirDocumentoBoletimMedicao(
                this.contrato.contratoId,
                documentoBoletimMedicao.boletimMedicaoId,
                documentoBoletimMedicao.documentoBoletimMedicaoId
            )
                .then(response => {
                    if (response && response.success) {
                        this.$toast.success('Anexo excluído com sucesso!', 'OK', this.$toastConfig.success)
                        this.$emit('recarregarDadosBoletim')
                        return
                    }

                    this.$toast.error('Não foi possível excluir o anexo', 'Erro', this.$toastConfig.error)
                })
                .catch(error => {
                    this.$toast.error('Não foi possível excluir o anexo', 'Erro', this.$toastConfig.error)
                })
                .finally(() => {
                    this.$store.dispatch('removeRequest')
                })
        },

        baixarTodos(){
            this.anexosMedicao.forEach(anexo => this.downloadAnexo(anexo))
        },
    },
}
</script>
